import { NgTemplateOutlet } from '@angular/common';
import { afterNextRender, Component, ElementRef, inject, input, output } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { ContextTooltipContainerDirective } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorGroupedConfig,
  ProsemirrorGroupedConfigDirective,
  WysiwygComponent,
} from '@core/prosemirror-legacy/webapp';
import type { EditorJSONState } from '@core/prosemirror-types';
import { BoxComponent, FormFieldComponent } from '@core/user-interface-legacy';
import type { RedactorConfig } from '@core/wysiwyg-legacy';
import { RedactorComponent, RedactorService } from '@core/wysiwyg-legacy';

@Component({
  selector: 'rt-evaluation-comment-field',
  templateUrl: './evaluation-comment-field.component.html',
  styleUrls: ['./evaluation-comment-field.component.less'],
  imports: [
    NgTemplateOutlet,
    BoxComponent,
    FormFieldComponent,
    ReactiveFormsModule,
    RedactorComponent,
    TranslatePipe,
    WysiwygComponent,
    ProsemirrorGroupedConfigDirective,
    ContextTooltipContainerDirective,
  ],
})
export class EvaluationCommentFieldComponent {
  private readonly redactorService = inject(RedactorService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly redactorConfig: RedactorConfig = {
    ...this.redactorService.defaultConfig,
    autoparseVideo: false,
    minHeight: '100px',
    maxHeight: '600px',
  };

  protected readonly ProsemirrorGroupedConfig = ProsemirrorGroupedConfig;
  protected readonly useProsemirror = injectDevFlag(DevFlag.ProsemirrorInEvaluations);

  public readonly control = input.required<FormControl<string | null>>();
  public readonly jsonControl = input.required<FormControl<EditorJSONState | null>>();
  public readonly hasBox = input<boolean>(true);
  public readonly scrollOnCreate = input<boolean>(false);

  public readonly scrolledOnCreate = output<void>();

  constructor() {
    afterNextRender(() => {
      if (!this.scrollOnCreate()) return;

      this.elementRef.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      this.scrolledOnCreate.emit();
    });
  }
}
